/**
 * Centralized Logging Utility
 *
 * This utility provides a safe way to control console logging across the application.
 * Logs can be disabled completely for production or filtered by level.
 *
 * All console.* calls throughout the app are routed through this logger.
 */

export enum LogLevel {
  ERROR = 0,
  WARN = 1,
  INFO = 2,
  DEBUG = 3,
  VERBOSE = 4
}

interface LoggerConfig {
  enabled: boolean
  level: LogLevel
  enabledCategories: Set<string>
  disabledCategories: Set<string>
}

// Store original console methods BEFORE any overrides
// This is used by the logger to actually output to console
const ORIGINAL_CONSOLE = {
  log: console.log.bind(console),
  info: console.info.bind(console),
  warn: console.warn.bind(console),
  debug: console.debug.bind(console),
  error: console.error.bind(console),
  table: console.table.bind(console),
  group: console.group.bind(console),
  groupEnd: console.groupEnd.bind(console),
  groupCollapsed: console.groupCollapsed.bind(console)
}

// Expose original console globally for console override to use
;(window as any).__ORIGINAL_CONSOLE__ = ORIGINAL_CONSOLE

class Logger {
  private config: LoggerConfig

  constructor() {
    this.config = {
      enabled: this.shouldEnableLogging(),
      level: this.getLogLevel(),
      enabledCategories: new Set(['staking', 'wallet', 'ui-state', 'validator', 'api', 'rpc']), // Enable all categories
      disabledCategories: new Set() // No disabled categories
    }
  }

  private shouldEnableLogging(): boolean {
    // Check environment variables for explicit configuration
    if (import.meta.env.VITE_ENABLE_LOGGING === 'true') return true
    if (import.meta.env.VITE_DISABLE_LOGGING === 'true') return false

    // DISABLE all browser logging for production (silent by default)
    return false
  }

  private getLogLevel(): LogLevel {
    // Check environment variable for log level
    const envLevel = import.meta.env.VITE_LOG_LEVEL
    if (envLevel === 'ERROR') return LogLevel.ERROR
    if (envLevel === 'WARN') return LogLevel.WARN
    if (envLevel === 'INFO') return LogLevel.INFO
    if (envLevel === 'DEBUG') return LogLevel.DEBUG
    if (envLevel === 'VERBOSE') return LogLevel.VERBOSE

    // Default to VERBOSE level (show all logs)
    return LogLevel.VERBOSE
  }

  private shouldLog(level: LogLevel, category?: string): boolean {
    // Enable all logging
    if (!this.config.enabled) return false
    if (level > this.config.level) return false

    // Check category filters
    if (category) {
      if (this.config.disabledCategories.has(category)) return false
      if (this.config.enabledCategories.size > 0 && !this.config.enabledCategories.has(category)) return false
    }

    return true
  }

  error(message: string, ...args: any[]): void {
    if (this.shouldLog(LogLevel.ERROR)) {
      ORIGINAL_CONSOLE.error(message, ...args)
    }
  }

  warn(message: string, ...args: any[]): void {
    if (this.shouldLog(LogLevel.WARN)) {
      ORIGINAL_CONSOLE.warn(message, ...args)
    }
  }

  info(message: string, category?: string, ...args: any[]): void {
    if (this.shouldLog(LogLevel.INFO, category)) {
      ORIGINAL_CONSOLE.log(`ℹ️ [INFO] ${message}`, ...args)
    }
  }

  debug(message: string, category?: string, ...args: any[]): void {
    if (this.shouldLog(LogLevel.DEBUG, category)) {
      ORIGINAL_CONSOLE.log(`🔍 [DEBUG] ${message}`, ...args)
    }
  }

  verbose(message: string, category?: string, ...args: any[]): void {
    if (this.shouldLog(LogLevel.VERBOSE, category)) {
      ORIGINAL_CONSOLE.log(`📝 [VERBOSE] ${message}`, ...args)
    }
  }

  // Utility methods for common categories
  staking(message: string, ...args: any[]): void {
    this.verbose(message, 'staking', ...args)
  }

  wallet(message: string, ...args: any[]): void {
    this.verbose(message, 'wallet', ...args)
  }

  validator(message: string, ...args: any[]): void {
    if (this.shouldLog(LogLevel.VERBOSE, 'validator')) {
      ORIGINAL_CONSOLE.log(`🔍 [VALIDATOR] ${message}`, ...args)
    }
  }

  api(message: string, ...args: any[]): void {
    this.debug(message, 'api', ...args)
  }

  rpc(message: string, ...args: any[]): void {
    this.debug(message, 'rpc', ...args)
  }

  uiState(message: string, ...args: any[]): void {
    this.verbose(message, 'ui-state', ...args)
  }

  // Configuration methods
  enableCategory(category: string): void {
    this.config.disabledCategories.delete(category)
    this.config.enabledCategories.add(category)
  }

  disableCategory(category: string): void {
    this.config.enabledCategories.delete(category)
    this.config.disabledCategories.add(category)
  }

  setLogLevel(level: LogLevel): void {
    this.config.level = level
  }

  enable(): void {
    this.config.enabled = true
  }

  disable(): void {
    this.config.enabled = false
  }

  getConfig(): LoggerConfig {
    return { ...this.config }
  }
}

// Export singleton instance
export const logger = new Logger()

// Expose logger globally for console override to use
;(window as any).__CENTRALIZED_LOGGER__ = logger

// Logger initialization (test logs disabled to keep console clean)

// Export convenience methods
export const log = {
  error: (message: string, ...args: any[]) => logger.error(message, ...args),
  warn: (message: string, ...args: any[]) => logger.warn(message, ...args),
  info: (message: string, ...args: any[]) => logger.info(message, undefined, ...args),
  debug: (message: string, ...args: any[]) => logger.debug(message, undefined, ...args),

  // Category-specific logging
  staking: (message: string, ...args: any[]) => logger.staking(message, ...args),
  wallet: (message: string, ...args: any[]) => logger.wallet(message, ...args),
  validator: (message: string, ...args: any[]) => logger.validator(message, ...args),
  api: (message: string, ...args: any[]) => logger.api(message, ...args),
  rpc: (message: string, ...args: any[]) => logger.rpc(message, ...args),
  uiState: (message: string, ...args: any[]) => logger.uiState(message, ...args)
}

export default logger